import React, { useEffect, useState } from 'react';

/* ------------------------------------------------------------ formatting */

export const inr = (value, compact = false) => {
  const n = Number(value) || 0;
  if (compact) {
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  }
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

export const num = value => Number(value || 0).toLocaleString('en-IN');

export const dateStr = value =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

export const dateTimeStr = value =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export const timeAgo = value => {
  if (!value) return '—';
  const seconds = Math.floor((Date.now() - new Date(value)) / 1000);
  if (seconds < 60) return 'just now';
  const units = [
    [60, 'min'],
    [24, 'hr'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
  ];
  let value2 = seconds / 60;
  let unit = 'min';
  for (let i = 1; i < units.length; i += 1) {
    if (value2 < units[i][0]) break;
    value2 /= units[i][0];
    [, unit] = units[i];
  }
  const rounded = Math.floor(value2);
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ago`;
};

export const titleCase = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

export const initials = name =>
  String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

/* ---------------------------------------------------------------- atoms */

export const Card = ({ title, subtitle, actions, children, className = '', bodyClass = '', ...rest }) => (
  <div className={`card ${className}`} {...rest}>
    {(title || actions) && (
      <div className="card-head">
        <div>
          {title && <div className="card-title">{title}</div>}
          {subtitle && <div className="card-sub">{subtitle}</div>}
        </div>
        {actions && <div className="row">{actions}</div>}
      </div>
    )}
    <div className={`card-body ${bodyClass}`}>{children}</div>
  </div>
);

export const Button = ({
  variant = 'primary',
  size,
  loading = false,
  icon,
  block,
  children,
  className = '',
  ...rest
}) => (
  <button
    type="button"
    className={`btn btn-${variant} ${size ? `btn-${size}` : ''} ${block ? 'btn-block' : ''} ${className}`}
    disabled={loading || rest.disabled}
    {...rest}
  >
    {loading ? <span className={`spinner ${variant !== 'primary' ? 'dark' : ''}`} /> : icon}
    {children}
  </button>
);

export const IconButton = ({ icon, title, className = '', ...rest }) => (
  <button type="button" className={`icon-btn ${className}`} title={title} {...rest}>
    {icon}
  </button>
);

export const Field = ({ label, hint, error, children }) => (
  <div className="field">
    {label && <label>{label}</label>}
    {children}
    {error ? <div className="err">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
  </div>
);

export const Input = ({ label, hint, error, lead, className = '', ...rest }) => {
  // className has to be merged, not spread: leaving it in `rest` let a caller's
  // class replace "input" outright, which stripped every credential field on
  // the Settings screen back to unstyled browser chrome.
  const control = <input className={`input ${className}`.trim()} {...rest} />;
  return (
    <Field label={label} hint={hint} error={error}>
      {lead ? (
        <div className="input-group">
          <span className="lead">{lead}</span>
          {control}
        </div>
      ) : (
        control
      )}
    </Field>
  );
};

export const Textarea = ({ label, hint, error, className = '', ...rest }) => (
  <Field label={label} hint={hint} error={error}>
    <textarea className={`textarea ${className}`.trim()} {...rest} />
  </Field>
);

export const Select = ({ label, hint, error, options = [], placeholder, children, className = '', ...rest }) => (
  <Field label={label} hint={hint} error={error}>
    <select className={`select ${className}`.trim()} {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {children ||
        options.map(opt => {
          const value = typeof opt === 'string' ? opt : opt.value;
          const text = typeof opt === 'string' ? titleCase(opt) : opt.label;
          return (
            <option key={value} value={value}>
              {text}
            </option>
          );
        })}
    </select>
  </Field>
);

export const Switch = ({ checked, onChange, label, disabled }) => (
  <label className="switch">
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
    <span className="track" />
    {label && <span className="switch-label">{label}</span>}
  </label>
);

export const Segment = ({ value, onChange, options }) => (
  <div className="segment">
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        className={value === opt.value ? 'active' : ''}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

/** Maps every domain status to a badge tone in one place. */
const TONES = {
  approved: 'success', active: 'success', delivered: 'success', paid: 'success',
  verified: 'success', settled: 'success', accepted: 'success', completed: 'success',
  closed: 'success', picked_up: 'info', in_transit: 'info', out_for_delivery: 'info',
  dispatched: 'info', packed: 'info', placed: 'info', sent: 'info', assigned: 'info',
  quoted: 'info', requested: 'warn', pending: 'warn', under_review: 'warn',
  raised: 'warn', partial: 'warn', draft: 'muted', expired: 'muted',
  rejected: 'danger', cancelled: 'danger', failed: 'danger', revoked: 'danger',
  terminated: 'danger', blocked: 'danger', renewed: 'brand', refunded: 'brand',
};

export const Badge = ({ status, children, tone, dot = true }) => {
  const key = String(status || '').toLowerCase();
  const resolved = tone || TONES[key] || 'muted';
  return (
    <span className={`badge badge-${resolved}`}>
      {dot && <span className="dot" />}
      {children || titleCase(status)}
    </span>
  );
};

export const Avatar = ({ src, name, size = '' }) =>
  src ? (
    <img className={`avatar ${size}`} src={src} alt={name || ''} />
  ) : (
    <div className={`avatar ${size}`}>{initials(name)}</div>
  );

export const IdCell = ({ image, name, sub, size }) => (
  <div className="id-cell">
    <Avatar src={image} name={name} size={size} />
    <div className="meta">
      <div className="name">{name || '—'}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  </div>
);

export const StatCard = ({ label, value, icon, foot, accent, onClick }) => (
  <div className={`stat ${accent ? 'accent' : ''}`} onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
    {icon && <div className="ico-badge">{icon}</div>}
    <div className="label">{label}</div>
    <div className="value">{value}</div>
    {foot && <div className="foot">{foot}</div>}
  </div>
);

export const EmptyState = ({ icon = '🗂', title = 'Nothing here yet', message, action }) => (
  <div className="empty">
    <div className="art">{icon}</div>
    <h3>{title}</h3>
    {message && <p style={{ maxWidth: 380, margin: '0 auto' }}>{message}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

export const Loader = ({ label = 'Loading…' }) => (
  <div className="loader-center">
    <div style={{ textAlign: 'center' }}>
      <div className="spinner dark" style={{ width: 26, height: 26, margin: '0 auto 10px' }} />
      <div className="text-muted text-sm">{label}</div>
    </div>
  </div>
);

export const SkeletonRows = ({ rows = 6, cols = 5 }) => (
  <div className="table-wrap">
    <div style={{ padding: 15 }}>
      {Array.from({ length: rows }).map((unused, r) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={r} className="row" style={{ gap: 16, marginBottom: 14 }}>
          {Array.from({ length: cols }).map((unused2, c) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={c} className="skeleton" style={{ flex: c === 0 ? 2 : 1 }} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* --------------------------------------------------------------- modals */

export const Modal = ({ open, title, subtitle, onClose, children, footer, size = '' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size}`} role="dialog" aria-modal="true">
        <div className="modal-head">
          <div>
            <div className="card-title">{title}</div>
            {subtitle && <div className="card-sub">{subtitle}</div>}
          </div>
          <IconButton icon="✕" onClick={onClose} title="Close" />
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
};

export const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', danger, onConfirm, onClose, loading }) => (
  <Modal
    open={open}
    title={title}
    onClose={onClose}
    footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    <p className="text-muted">{message}</p>
  </Modal>
);

/* ---------------------------------------------------------------- table */

export const Pagination = ({ meta, onChange }) => {
  if (!meta || meta.pages <= 1) return null;

  const { page, pages, total, limit } = meta;
  const window0 = [];
  const from = Math.max(1, page - 2);
  const to = Math.min(pages, from + 4);
  for (let i = from; i <= to; i += 1) window0.push(i);

  return (
    <div className="pagination">
      <div className="info">
        Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {num(total)}
      </div>
      <div className="pages">
        <button type="button" className="page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
        {from > 1 && (
          <>
            <button type="button" className="page-btn" onClick={() => onChange(1)}>1</button>
            {from > 2 && <span className="page-btn" style={{ border: 'none', background: 'none' }}>…</span>}
          </>
        )}
        {window0.map(p => (
          <button key={p} type="button" className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => onChange(p)}>
            {p}
          </button>
        ))}
        {to < pages && (
          <>
            {to < pages - 1 && <span className="page-btn" style={{ border: 'none', background: 'none' }}>…</span>}
            <button type="button" className="page-btn" onClick={() => onChange(pages)}>{pages}</button>
          </>
        )}
        <button type="button" className="page-btn" disabled={page === pages} onClick={() => onChange(page + 1)}>›</button>
      </div>
    </div>
  );
};

/**
 * Declarative table. `columns` items look like:
 *   { key, label, render?, align?, width? }
 */
export const DataTable = ({ columns, rows, loading, meta, onPage, onRowClick, empty }) => {
  if (loading) return <SkeletonRows cols={columns.length} />;

  if (!rows || !rows.length) {
    return (
      <div className="table-wrap">
        {empty || <EmptyState message="No records match the current filters." />}
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{ textAlign: col.align || 'left', width: col.width }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="stagger">
          {rows.map((row, index) => (
            <tr
              key={row._id || index}
              className={onRowClick ? 'clickable' : ''}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map(col => (
                <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                  {col.render ? col.render(row, index) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {meta && onPage && <Pagination meta={meta} onChange={onPage} />}
    </div>
  );
};

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="tabs">
    {tabs.map(tab => (
      <button
        key={tab.value}
        type="button"
        className={`tab ${value === tab.value ? 'active' : ''}`}
        onClick={() => onChange(tab.value)}
      >
        {tab.label}
        {tab.count !== undefined && tab.count !== null ? ` (${tab.count})` : ''}
      </button>
    ))}
  </div>
);

export const PageHead = ({ title, subtitle, actions }) => (
  <div className="page-head">
    <div>
      <h1>{title}</h1>
      {subtitle && <div className="sub">{subtitle}</div>}
    </div>
    {actions && <div className="row wrap">{actions}</div>}
  </div>
);

/** Debounced search box — avoids a request per keystroke. */
export const SearchBox = ({ value, onChange, placeholder = 'Search…', delay = 350 }) => {
  const [local, setLocal] = useState(value || '');

  useEffect(() => setLocal(value || ''), [value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (local !== value) onChange(local);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="input-group search">
      <span className="lead">⌕</span>
      <input
        className="input"
        value={local}
        placeholder={placeholder}
        onChange={e => setLocal(e.target.value)}
      />
    </div>
  );
};

export const KV = ({ items }) => (
  <dl className="kv">
    {items
      .filter(item => item)
      .map(item => (
        <React.Fragment key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value ?? '—'}</dd>
        </React.Fragment>
      ))}
  </dl>
);
